import { Heading, Subheading } from "../../../design/heading";
import { useAnalysis } from "../../../contexts/AnalysisContext";
import { Text } from "../../../design/text";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Navbar, NavbarItem } from "../../../design/navbar";
import { useState } from "react";
import EmptyStateView from "../../../components/EmptyStateView";

interface AnalysisOverviewProps {}

const AnalysisOverview: React.FC<AnalysisOverviewProps> = () => {
  const { analysis } = useAnalysis();
  const [state, setState] = useState<
    | "recommendations"
    | "justification"
    | "evidenceAnalysis"
    | "legalConsiderations"
  >("recommendations");

  const getColor = (percentage: number) => {
    if (percentage >= 70) return "#10B981"; // Green
    if (percentage >= 40) return "#FACC15"; // Yellow
    return "#EF4444"; // Red
  };

  const pieData = [
    { name: "Success", value: analysis?.likelihoodOfSuccess ?? 0 },
    { name: "Remaining", value: 100 - (analysis?.likelihoodOfSuccess ?? 0) },
  ];

  return (
    <div
      className="p-4 pt-4 my-4 bg-white border rounded-md shadow-lg dark:bg-black"
      id="matter_overview_analysis"
    >
      <Heading>Analysis</Heading>
      {!analysis && (
        <EmptyStateView
          title="No analysis available"
          message="There is no analysis available for this matter."
        />
      )}
      {analysis?.likelihoodOfSuccess && (
        <div className="dark:bg-black">
          <div className="flex flex-col items-center mb-6">
            <h3 className="mb-2 text-lg font-medium">Matter Strength</h3>
            <div className="relative w-32 h-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    innerRadius={50}
                    outerRadius={60}
                    startAngle={90}
                    endAngle={-270}
                    stroke="none"
                  >
                    <Cell
                      key="success"
                      fill={getColor(analysis.likelihoodOfSuccess)}
                    />
                    <Cell key="remaining" fill="#E5E7EB" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <Heading className="absolute inset-0 flex items-center justify-center text-xl font-semibold text-gray-700">
                {analysis.likelihoodOfSuccess}%
              </Heading>
            </div>
          </div>
          {analysis.recommendations && (
            <>
              <Navbar>
                <NavbarItem
                  onClick={() => setState("recommendations")}
                  current={state === "recommendations"}
                >
                  Recommendations
                </NavbarItem>
                <NavbarItem
                  onClick={() => setState("justification")}
                  current={state === "justification"}
                >
                  Justification
                </NavbarItem>
                <NavbarItem
                  onClick={() => setState("evidenceAnalysis")}
                  current={state === "evidenceAnalysis"}
                >
                  Evidence
                </NavbarItem>
                <NavbarItem
                  onClick={() => setState("legalConsiderations")}
                  current={state === "legalConsiderations"}
                >
                  Considerations
                </NavbarItem>
              </Navbar>

              {state === "recommendations" && (
                <div className="mb-4">
                  <div className="mt-2">
                    <Subheading>Improvements</Subheading>
                    <ul className="text-gray-700 list-disc list-inside">
                      {analysis.recommendations?.improvements.map(
                        (item, index) => <Text key={index}>• {item}</Text>,
                      )}
                    </ul>
                  </div>
                  <div className="mt-2">
                    <Subheading>Risks</Subheading>
                    <ul className="text-gray-700 list-disc list-inside">
                      {analysis.recommendations.risks.map((item, index) => (
                        <Text key={index}>• {item}</Text>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {state === "justification" && (
                <div className="mb-4">
                  <div className="mt-2">
                    <Subheading>Key Strengths</Subheading>
                    <ul className="text-gray-700 list-disc list-inside">
                      {analysis.justification.keyStrengths.map(
                        (item, index) => (
                          <Text key={index}>• {item}</Text>
                        ),
                      )}
                    </ul>
                  </div>
                  <div className="mt-2">
                    <Subheading>Key Weaknesses</Subheading>
                    <ul className="text-gray-700 list-disc list-inside">
                      {analysis.justification.keyWeaknesses.map(
                        (item, index) => (
                          <Text key={index}>• {item}</Text>
                        ),
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {state === "evidenceAnalysis" && (
                <div className="mb-4">
                  <div className="mt-2">
                    <Subheading>Qualitative</Subheading>
                    <ul className="text-gray-700 list-disc list-inside">
                      {analysis.justification.evidenceAnalysis.qualitative.map(
                        (item, index) => (
                          <Text key={index}>• {item}</Text>
                        ),
                      )}
                    </ul>
                  </div>
                  <div className="mt-2">
                    <Subheading>Quantitative</Subheading>
                    <ul className="text-gray-700 list-disc list-inside">
                      {analysis.justification.evidenceAnalysis.quantitative.map(
                        (item, index) => (
                          <Text key={index}>• {item}</Text>
                        ),
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {state === "legalConsiderations" && (
                <div className="mt-2">
                  <ul className="text-gray-700 list-disc list-inside">
                    {analysis.justification.legalConsiderations.map(
                      (item, index) => (
                        <Text key={index}>• {item}</Text>
                      ),
                    )}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
export default AnalysisOverview;
